/**
 * Theme sync: 2026-09-03-base
 * jrs-v2-flow-multistep.js — Multistep PDP flow orchestrator (Phase 2)
 *
 * Doc 6 §11.2 flow orchestrator role.
 *
 * Sequence:
 *   intro → position_picker → application_method
 *         → [logo_strategy if positions.length >= 2 else skip]
 *         → logo_picker (loops if logo_strategy === 'different_logos')
 *         → review
 *
 * The shell exposes goToStep / goBack; this orchestrator is the place
 * where flow-level decisions live. Most step controllers in Phase 2 also
 * encode the immediate-next-step logic locally for self-contained
 * behaviour pre-orchestrator; this module is the canonical owner and
 * supersedes those local heuristics by being the caller from the PDP
 * trigger.
 *
 * Entry point:
 *   JRS.FlowMultistep.start(context)
 *     - context: { productId, productHandle, productCategoryHandle,
 *                  productTags, productPricing }
 *     - Calls JRS.CustomizerPopup.open() with entryStep='intro',
 *       flow='multistep', and the context flowed through to step
 *       controllers via _cachedPageContext on the shell.
 *
 * Per-position repetition of logo_picker is handled via the internal
 * fields state.draft._position_cursor (orchestrator-owned) and
 * state.draft._current_position_for_logo (orchestrator-set before each
 * logo_picker entry).
 *
 * The orchestrator is stateless except for subscriptions; all flow data
 * lives in state.draft.
 */

window.JRS = window.JRS || {};

(function (JRS) {
  'use strict';

  var FLOW_ID = 'multistep';

  // Estimated total steps (excluding conditional logo_strategy). The shell
  // uses this for the progress bar; controllers may dispatch
  // SET_TOTAL_STEPS to refine it.
  var TOTAL_STEPS_DEFAULT = 5; // intro + position_picker + application_method + logo_picker + review

  // 2026-05-20 Bug 5 fix — dynamic step counter.
  //
  // The popup's progress bar previously showed garbage like "Step 10 of 5"
  // because TOTAL_STEPS_DEFAULT was static and the reducer just
  // incremented currentStepIndex by 1 on every goToStep. With conditional
  // logo_strategy (positions >= 2) and per-position logo_picker looping
  // under different_logos, the actual step count depends on state.draft.
  // These two pure helpers compute the right values; the shell calls
  // them on every step transition to keep the counter honest.

  /**
   * How many steps the user will see end-to-end for the current draft.
   * @param {Object} state - JRS.CustomizerPopupState snapshot
   * @returns {number}
   */
  function computeTotalSteps(state) {
    var draft = (state && state.draft) || {};
    var positions = draft.positions || [];
    var posCount = positions.length;
    var strategy = draft.logo_strategy;

    // Initial / pre-position_picker default: show the minimum flow length
    // (single position, no logo_strategy choice yet) so the progress bar
    // doesn't read "1 of 4" at the very start. position_picker's onExit
    // re-corrects this once posCount is known.
    if (posCount === 0) return 5;

    // intro + position_picker + application_method + review = 4
    var total = 4;
    // logo_strategy only when positions.length >= 2
    if (posCount >= 2) total += 1;

    // logo_picker iterations:
    //   1 position : 1 (no logo_strategy choice yet)
    //   N+ same_logo: 1
    //   N+ different_logos: N (one per position)
    //   N+ strategy not yet chosen: 1 (assume same_logo until it's set)
    var pickerCount;
    if (posCount === 1) {
      pickerCount = 1;
    } else if (strategy === 'different_logos') {
      pickerCount = posCount;
    } else {
      pickerCount = 1;
    }
    total += pickerCount;

    return total;
  }

  /**
   * Where the given step sits in the flow for the current draft. 1-based.
   * For logo_picker, the index advances per cursor iteration (cursor 0 →
   * baseIndex, cursor 1 → baseIndex+1, ...). Bug 3's same-step re-entry
   * uses this to bump the visible index across logo_picker loops.
   *
   * @param {string} stepId
   * @param {Object} state - JRS.CustomizerPopupState snapshot
   * @returns {number} 1-based; returns 0 for unknown steps
   */
  function stepIndexFor(stepId, state) {
    var draft = (state && state.draft) || {};
    var positions = draft.positions || [];
    var posCount = positions.length;
    var strategy = draft.logo_strategy;
    var hasStrategy = posCount >= 2;

    switch (stepId) {
      case 'intro':              return 1;
      case 'position_picker':    return 2;
      case 'application_method': return 3;
      case 'logo_strategy':      return hasStrategy ? 4 : 0;
      case 'logo_picker': {
        var baseLogoPickerIndex = hasStrategy ? 5 : 4;
        var cursor = typeof draft._position_cursor === 'number' ? draft._position_cursor : 0;
        if (strategy === 'different_logos' && cursor > 0 && cursor < posCount) {
          return baseLogoPickerIndex + cursor;
        }
        return baseLogoPickerIndex;
      }
      case 'logo_text_builder': {
        // text-builder is a sub-step of logo_picker — share the index so
        // the progress bar doesn't visibly jump backward when entering
        // text mode.
        var baseLogoPickerIndex2 = hasStrategy ? 5 : 4;
        var cursor2 = typeof draft._position_cursor === 'number' ? draft._position_cursor : 0;
        if (strategy === 'different_logos' && cursor2 > 0 && cursor2 < posCount) {
          return baseLogoPickerIndex2 + cursor2;
        }
        return baseLogoPickerIndex2;
      }
      case 'review':             return computeTotalSteps(state);
      default:                   return 0;
    }
  }

  /**
   * Public entry: open the popup and start the multistep flow.
   *
   * @param {Object} context - PDP context to pass to the shell
   */
  function start(context) {
    if (!JRS.CustomizerPopup || typeof JRS.CustomizerPopup.open !== 'function') {
      console.error('JRS.FlowMultistep.start: JRS.CustomizerPopup is not available');
      return;
    }

    JRS.CustomizerPopup.open({
      flow: FLOW_ID,
      source: 'pdp',
      trigger: 'flow_start',
      entryStep: 'intro',
      entryStepIndex: 1,
      totalSteps: TOTAL_STEPS_DEFAULT,
      context: context || {}
    });
  }

  /**
   * Compute the next step given the current step and current state.
   * Exposed for the shell or step controllers that want to defer the
   * decision to the orchestrator.
   *
   * @param {string} currentStep
   * @param {Object} state - JRS.CustomizerPopupState snapshot
   * @returns {string|null} next step id, or null if terminal
   */
  function nextStep(currentStep, state) {
    var draft = (state && state.draft) || {};
    var positions = draft.positions || [];

    switch (currentStep) {
      case 'intro':
        return draft.intro_choice === 'yes' ? 'position_picker' : null;

      case 'position_picker':
        return 'application_method';

      case 'application_method':
        if (positions.length >= 2) return 'logo_strategy';
        // Single position skips logo_strategy.
        _prepareLogoPickerCursor(draft, 0);
        return 'logo_picker';

      case 'logo_strategy':
        _prepareLogoPickerCursor(draft, 0);
        return 'logo_picker';

      case 'logo_picker': {
        // If strategy='different_logos' and more positions remain, loop.
        if (draft.logo_strategy === 'different_logos') {
          var nextCursor = (typeof draft._position_cursor === 'number' ? draft._position_cursor : 0) + 1;
          if (nextCursor < positions.length) {
            _prepareLogoPickerCursor(draft, nextCursor);
            return 'logo_picker';
          }
        }
        return 'review';
      }

      case 'logo_text_builder':
        // text-builder is a sub-step accessed FROM logo_picker; on continue
        // it navigates back to logo_picker so the same logo_picker post-CTA
        // logic applies. Orchestrator wouldn't normally compute next for
        // this step. Return logo_picker as a defensive default.
        return 'logo_picker';

      case 'review':
        return null;

      default:
        return null;
    }
  }

  function _prepareLogoPickerCursor(draft, cursor) {
    var positions = draft.positions || [];
    if (positions.length === 0) return;
    var safeCursor = Math.max(0, Math.min(cursor, positions.length - 1));
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT',
      payload: {
        draft: _merge(draft, {
          _position_cursor: safeCursor,
          _current_position_for_logo: positions[safeCursor]
        })
      }
    });
  }

  function _merge(a, b) {
    var out = {};
    for (var k1 in a) {
      if (Object.prototype.hasOwnProperty.call(a, k1)) out[k1] = a[k1];
    }
    for (var k2 in b) {
      if (Object.prototype.hasOwnProperty.call(b, k2)) out[k2] = b[k2];
    }
    return out;
  }

  // -------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------

  JRS.FlowMultistep = {
    FLOW_ID: FLOW_ID,
    start: start,
    nextStep: nextStep,
    computeTotalSteps: computeTotalSteps,
    stepIndexFor: stepIndexFor
  };
})(window.JRS);
